import { useInView } from 'react-intersection-observer';
import SkeletonRect from './skeleton/SkeletonRect';
import { ReactNode } from 'react';
import { DynamicOptionsLoadingProps } from 'next/dynamic';

export const loading = ({
  height,
  width,
}: {
  width?: string;
  /** Expected height of the final component. If you are using `LazyComponent` make sure the heights are the same  */
  height: string;
}) =>
  function Loading(props: DynamicOptionsLoadingProps) {
    if (props.isLoading) {
      return (
        <div
          style={{
            width: width,
            height: height,
          }}
        >
          <SkeletonRect />
        </div>
      );
    }
    if (props.error || props.timedOut) {
      return (
        <div
          style={{
            width: width,
            height: height,
          }}
        >
          Failed to load component.
        </div>
      );
    }
    return null;
  };

type Props = {
  id?: string;
  /** Expected height of the final component */
  height: string;
  width?: string;
  /** The number of pixels that the element's top needs to be within the viewport to trigger the load.
   * @default 500
   */
  loadOffset?: number;
  children?: ReactNode;
};

export default function LazyComponent({
  id,
  loadOffset = 500,
  children,
  width = 'auto',
  height = 'auto',
}: Props) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: `${loadOffset}px 0px`,
  });

  return (
    <div id={id} ref={ref}>
      {inView ? (
        children
      ) : (
        <div
          style={{
            width: width,
            height: height,
            margin: '0 auto',
          }}
        >
          <SkeletonRect />
        </div>
      )}
    </div>
  );
}
